import { subscribeCicdEvents } from "../../../hooks/useCicdEvents";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../utils/helpers";
import { useTransportV2 } from "../context/TransportV2Context";
import { useLang } from "../i18n";
import { transportBatchError } from "../../../utils/transportBatchErrors";

export function useBatchTransport() {
  const { state, dispatch } = useTransportV2();
  const { lang } = useLang();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [recent, setRecent] = useState([]);
  const inFlight = useRef(false);
  // A retry after an uncertain HTTP response repeats the same request, not a second transport.
  const request = useRef(state.batchRequest || null);
  const batch = state.batch;
  const accept = useCallback(response => {
    const value = response?.batch || response;
    if (!value?.id) throw Object.assign(new Error("Invalid batch response"), { code: "BATCH_RESPONSE_INVALID" });
    dispatch({ type: "SET_BATCH", payload: value });
    return value;
  }, [dispatch]);
  useEffect(() => {
    let cancelled = false;
    if (!state.spaceId) return;
    api(`/api/cicd/transport/batches?spaceId=${encodeURIComponent(state.spaceId)}`)
      .then(response => { if (!cancelled) setRecent(Array.isArray(response?.batches) ? response.batches : Array.isArray(response) ? response : []); })
      .catch(e => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [state.spaceId]);
  useEffect(() => {
    if (!batch?.id || !state.spaceId) return;
    let cancelled = false;
    let loading = false;
    const poll = async () => {
      if (cancelled || loading) return;
      loading = true;
      try {
        const response = await api(`/api/cicd/transport/batches/${encodeURIComponent(batch.id)}?spaceId=${encodeURIComponent(state.spaceId)}`);
        if (!cancelled) { accept(response); setError(null); }
      } catch (e) { if (!cancelled) setError(e); }
      finally { loading = false; }
    };
    const initialRead = poll();
    const close = subscribeCicdEvents(state.spaceId, async () => {
      // An event during the mount read must read again after that snapshot.
      await initialRead;
      if (!cancelled) await poll();
    });
    return () => { cancelled = true; close(); };
  }, [batch?.id, state.spaceId, accept]);
  const start = useCallback(async payload => {
    if (inFlight.current || batch) return;
    inFlight.current = true;
    setBusy(true); setError(null);
    const firstAttempt = !request.current;
    if (!request.current) {
      request.current = { ...payload, requestId: crypto.randomUUID() };
      dispatch({ type: "SET_BATCH_REQUEST", payload: request.current });
    }
    try {
      return accept(await api("/api/cicd/transport/batches", {
        method: "POST", timeout: 180000, body: JSON.stringify(request.current),
      }));
    } catch (e) {
      // Validation/access failures on the first request happen before a batch is
      // stored. Keep the draft editable. An uncertain earlier request stays frozen.
      if (firstAttempt && [400, 403, 404, 409, 422].includes(e.status) && e.code !== "IDEMPOTENCY_CONFLICT") {
        request.current = null;
        dispatch({ type: "SET_BATCH_REQUEST", payload: null });
      }
      setError(e);
    }
    finally { inFlight.current = false; setBusy(false); }
  }, [accept, batch, dispatch]);
  const resume = useCallback(async id => {
    if (inFlight.current) return;
    setBusy(true); setError(null);
    try { accept(await api(`/api/cicd/transport/batches/${encodeURIComponent(id)}?spaceId=${encodeURIComponent(state.spaceId)}`)); }
    catch (e) { setError(e); }
    finally { setBusy(false); }
  }, [state.spaceId, accept]);
  return { batch, busy, errorCode: error?.code || error?.body?.code, error: error ? transportBatchError(error, lang) : null, recent, start, resume, uncertain: !!request.current && !batch };
}
